import { Controller } from "@hotwired/stimulus"
import { post } from "@rails/request.js"

// Asks the authenticator for whichever account it holds, then submits
// the assertion as an ordinary form post. No address is typed first —
// the credential says who it belongs to.
export default class extends Controller {
  static targets = ["credential", "failure", "failureMessage"]
  static values = { challengeUrl: String, unsupported: String, cancelled: String }

  async authenticate(event) {
    if (this.credentialTarget.value) return

    event.preventDefault()

    if (!window.PublicKeyCredential) {
      this.#fail(this.unsupportedValue)
      return
    }

    try {
      const options = await this.#options()
      const assertion = await navigator.credentials.get({ publicKey: options })

      this.credentialTarget.value = JSON.stringify(this.#serialise(assertion))
      this.element.requestSubmit()
    } catch {
      this.#fail(this.cancelledValue)
    }
  }

  async #options() {
    const response = await post(this.challengeUrlValue, { responseKind: "json" })
    const options = await response.json

    options.challenge = this.#decode(options.challenge)
    options.allowCredentials = (options.allowCredentials || []).map(
      (credential) => ({ ...credential, id: this.#decode(credential.id) })
    )

    // Without a hint, a browser holding no passkey for this site leads
    // with the QR code for signing in from a phone. Naming this device
    // puts the one already in the user's hands first. Browsers that do
    // not know the option ignore it.
    options.hints = ["client-device"]

    return options
  }

  #serialise(assertion) {
    return {
      type: assertion.type,
      id: assertion.id,
      rawId: this.#encode(assertion.rawId),
      authenticatorAttachment: assertion.authenticatorAttachment,
      clientExtensionResults: assertion.getClientExtensionResults(),
      response: {
        authenticatorData: this.#encode(assertion.response.authenticatorData),
        clientDataJSON: this.#encode(assertion.response.clientDataJSON),
        signature: this.#encode(assertion.response.signature),
        userHandle: assertion.response.userHandle
          ? this.#encode(assertion.response.userHandle)
          : null
      }
    }
  }

  #fail(message) {
    this.failureMessageTarget.textContent = message
    this.failureTarget.classList.remove("hidden")
  }

  // WebAuthn speaks base64url without padding; atob needs standard
  // base64 with it.
  #decode(value) {
    const base64 = value.replace(/-/g, "+").replace(/_/g, "/")
    const padded = base64.padEnd(Math.ceil(base64.length / 4) * 4, "=")

    return Uint8Array.from(atob(padded), (character) => character.charCodeAt(0))
  }

  #encode(buffer) {
    const binary = String.fromCharCode(...new Uint8Array(buffer))

    return btoa(binary)
      .replace(/\+/g, "-")
      .replace(/\//g, "_")
      .replace(/=/g, "")
  }
}
